import React, { useState, FormEvent, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const NotifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const appName = searchParams.get('app') || 'a Riyaz Tool';
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: `Notify Me - ${appName}`,
    message: `Hi Manmit,\n\nPlease let me know when ${appName} is released!`,
  });

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [honeypot, setHoneypot] = useState('');

  // Auto-fill subject and message based on the requested app
  useEffect(() => {
    setForm((f) => ({
      ...f,
      subject: `Notify Me - ${appName}`,
      message: `Hi Manmit,\n\nPlease let me know when ${appName} is released!`,
    }));
  }, [appName]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (honeypot) {
      setStatus('success');
      return;
    }
    setStatus('sending');

    try {
      const apiUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3000/api/contact'
        : 'https://manmitsinghmusic.com/api/contact';

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setStatus('success');
        setForm({ ...form, name: '', email: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-charcoal text-white flex flex-col font-sans">
      <Navbar />

      <main className="flex-grow pt-32 pb-20 px-6 flex items-center justify-center">
        <div className="max-w-xl w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Get Notified
            </h1>
            <p className="text-neutral-400">
              Sign up to receive an email as soon as <span className="text-gold font-medium">{appName}</span> is ready for you to use.
            </p>
          </div>

          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 backdrop-blur-sm">
            <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
              <input
                type="text"
                name="website_url"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {status === 'success' && (
                <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-4 rounded-lg text-sm text-center">
                  ✓ Thank you! You'll be the first to know when it drops.
                </div>
              )}

              {status === 'error' && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg text-sm text-center">
                  Something went wrong. Please try again or email directly.
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label className="text-xs font-mono text-neutral-400 uppercase tracking-wider ml-1">Your Name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all placeholder:text-neutral-600"
                  placeholder="John Doe"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-mono text-neutral-400 uppercase tracking-wider ml-1">Your Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all placeholder:text-neutral-600"
                  placeholder="john@example.com"
                />
              </div>

              <div className="flex flex-col gap-2 hidden">
                <label className="text-xs font-mono text-neutral-400 uppercase tracking-wider ml-1">Subject</label>
                <input
                  type="text"
                  required
                  value={form.subject}
                  readOnly
                  className="bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-neutral-500 cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-mono text-neutral-400 uppercase tracking-wider ml-1">Message (Optional)</label>
                <textarea
                  required
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all placeholder:text-neutral-600 min-h-[120px] resize-y"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="mt-2 w-full py-3.5 rounded-xl bg-gold text-charcoal font-semibold hover:bg-gold-bright transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(212,175,55,0.2)]"
              >
                {status === 'sending' ? 'Registering...' : 'Notify Me'}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="mt-1 w-full py-3 rounded-xl bg-transparent border border-white/10 text-neutral-400 font-medium hover:text-white hover:border-white/20 transition-all duration-300"
              >
                Back to App Directory
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotifyPage;
